import { Align, Button, Icon, Row, Theme } from '@busybusy/webapp-react-ui';
import { BulletinIcon, CloseIcon } from 'assets/icons';
import { video_thumbnail } from 'assets/images';
import classNames from 'classnames';
import Graylog from 'components/domain/analytics/Graylog/Graylog';
import ConditionalContainer from 'components/foundation/ConditionalContainer/ConditionalContainer';
import Thumbnail from 'components/foundation/Thumbnail/Thumbnail';
import IconButton from 'components/foundation/buttons/IconButton/IconButton';
import ConfirmationDialog from 'components/foundation/dialogs/ConfirmationDialog/ConfirmationDialog';
import HeaderDialog from 'components/foundation/dialogs/HeaderDialog/HeaderDialog';
import BulletinEnableForm from 'containers/bulletin/BulletinEnableForm/BulletinEnableForm';
import DailyProjectReportEnableForm from 'containers/daily-project-report/DailyProjectReportEnableForm/DailyProjectReportEnableForm';
import DocumentEnableForm from 'containers/documents/DocumentEnableForm/DocumentEnableForm';
import ProgressTrackingEnableForm from 'containers/progress-report/ProgressTrackingEnableForm/ProgressTrackingEnableForm';
import { useActiveMember, useOpenable } from 'hooks';
import useMemberUiSettingsUpdate from 'hooks/models/member-settings/useMemberUiSettingsUpdate';
import useMemberSettings from 'hooks/models/member/useMemberSettings';
import { useOrganizationSettings } from 'hooks/models/organization/settings/useOrganizationSettings/useOrganizationSettings';
import useOnMount from 'hooks/utils/useOnMount/useOnMount';
import { isNil } from 'lodash';
import { useEffect, useState } from 'react';
import AnalyticsEvent from 'utils/constants/analytics/analyticsEvent';
import { useFeatureFlags } from 'utils/features';
import LocalStore from 'utils/localStorageUtils';
import { t } from 'utils/localize';
import { EnablePremiumDialog } from '../EnablePremiumDialog/EnablePremiumDialog';
import './DashboardAnnouncement.scss';

export interface IDashboardAnnouncementProps {
  className?: string | object;
  theme: Theme.DARK | Theme.LIGHT;
}

export const latestAnnouncementVersion = '2023.3';
export const latestAnnouncementKey = `announcement-${latestAnnouncementVersion}-content`;

const announcementLinksLookup = {
  allReleases: 'https://fast.wistia.com/embed/channel/fwd5oxatzi',
  release2022Dot10: 'https://fast.wistia.net/embed/channel/fwd5oxatzi?wchannelid=fwd5oxatzi&wmediaid=smg5bapmq7',
  release2023Dot1: 'https://fast.wistia.net/embed/channel/fwd5oxatzi?wchannelid=fwd5oxatzi&wmediaid=hwriddjbot',
  release2023Dot2: 'https://busybusy.wistia.com/medias/0vx391szrs',
  releaseOverview: 'https://busybusy.wistia.com/medias/nlk0xt9sf7',
  release2023Dot2Part1: 'https://busybusy.wistia.com/medias/w96t5camye',
  release2023Dot2Part2: 'https://busybusy.wistia.com/medias/634pqm1bek',
  release2023Dot2Part3: 'https://busybusy.wistia.com/medias/rpf4jxivvi',
  release2023Dot2Part4: 'https://busybusy.wistia.com/medias/pvoqm3vyl7',
  release2023Dot2Part5: 'https://busybusy.wistia.com/medias/208aoiqnrq',
  bulletinOverviewFreeAndPro: 'https://busybusy.com/product-videos/?wchannelid=q3wum81pn9&wmediaid=h80kvnvhvz',
  bulletinOverviewPremium: 'https://fast.wistia.net/embed/channel/nhzxwhi095?wchannelid=nhzxwhi095&wmediaid=0q7k278zad',
  bulletinOverviewPremiumTrial:
    'https://fast.wistia.net/embed/channel/ycm5d84606?wchannelid=ycm5d84606&wmediaid=f7jhx2xvkc',
};

const DashboardAnnouncement = (props: IDashboardAnnouncementProps) => {
  const { className, theme } = props;
  const classes = classNames('dashboard-annoucement', theme, 'mt-6', 'px-8', 'pt-4', 'pb-4', className);
  const memberSettings = useMemberSettings();
  const organizationSettings = useOrganizationSettings();

  const member = useActiveMember();
  const isPro = useFeatureFlags('PRO');
  const isPremium = useFeatureFlags('PREMIUM');
  const isPremiumTrial = useFeatureFlags('PREMIUM_TRIAL');
  const hasDocumentsAccess = useFeatureFlags('DOCUMENTS');
  const hasBulletinAccess = useFeatureFlags('BULLETIN_FEATURE');
  const hasDailyProjectReportAccess = useFeatureFlags('DAILY_REPORTS');
  const hasProgressTrackingAccess = useFeatureFlags('WORK_ACCOMPLISHED');
  const [showDocumentsAction, setShowDocumentsAction] = useState(false);
  const [showBulletinAction, setShowBulletinAction] = useState(false);
  const [showDailyProjectReportAction, setShowDailyProjectReportAction] = useState(false);
  const [showProgressTrackingAction, setShowProgressTrackingAction] = useState(false);
  const updateUiSettings = useMemberUiSettingsUpdate();
  const memberSettingsAnnouncementVersion = memberSettings?.web?.ui?.hideAnnouncementTextForVersion;
  const showAnnouncement = memberSettingsAnnouncementVersion !== latestAnnouncementVersion;
  const canManageCompanySettings = member.position?.manageCompanySettings ?? false;

  const documentDialogOpenable = useOpenable();
  const documentRequestCompleteOpenable = useOpenable();
  const bulletinDialogOpenable = useOpenable();
  const bulletinRequestCompleteOpenable = useOpenable();
  const dailyProjectReportDialogOpenable = useOpenable();
  const dailyProjectReportRequestCompleteOpenable = useOpenable();
  const progressTrackingDialogOpenable = useOpenable();
  const progressTrackingRequestCompleteOpenable = useOpenable();
  const premiumDialogOpenable = useOpenable();

  function graylogEnableBulletin() {
    Graylog({
      shortMsg: AnalyticsEvent.announcementText.UPGRADE_REQUESTED,
      logObject: {
        _identifier: latestAnnouncementVersion,
        _member_id: member.id,
        _type: 'Enable Bulletin',
      },
    });
  }

  function graylogEnableDocuments() {
    Graylog({
      shortMsg: AnalyticsEvent.announcementText.UPGRADE_REQUESTED,
      logObject: {
        _identifier: latestAnnouncementVersion,
        _member_id: member.id,
        _type: 'Enable Documents',
      },
    });
  }

  function graylogEnableDailyProjectReports() {
    Graylog({
      shortMsg: AnalyticsEvent.announcementText.UPGRADE_REQUESTED,
      logObject: {
        _identifier: latestAnnouncementVersion,
        _member_id: member.id,
        _type: 'Enable Daily Project Reports',
      },
    });
  }

  function graylogEnableProgressTracking() {
    Graylog({
      shortMsg: AnalyticsEvent.announcementText.UPGRADE_REQUESTED,
      logObject: {
        _identifier: latestAnnouncementVersion,
        _member_id: member.id,
        _type: 'Enable Progress Tracking',
      },
    });
  }

  function graylogUpgradePremium() {
    Graylog({
      shortMsg: AnalyticsEvent.announcementText.UPGRADE_REQUESTED,
      logObject: {
        _identifier: latestAnnouncementVersion,
        _member_id: member.id,
        _type: 'Upgrade Premium',
      },
    });
  }

  const onClose = () => {
    updateUiSettings('hideAnnouncementTextForVersion', latestAnnouncementVersion);
  };

  useOnMount(() => {
    if (
      latestAnnouncementVersion !== memberSettingsAnnouncementVersion &&
      !isNil(LocalStore.get(latestAnnouncementKey)) &&
      LocalStore.get(latestAnnouncementKey) === true
    ) {
      updateUiSettings('hideAnnouncementTextForVersion', latestAnnouncementVersion);
    }
  });

  useEffect(() => {
    setShowDocumentsAction(!hasDocumentsAccess && organizationSettings?.web?.ui?.requestedDocumentsAccess !== true);
  }, [hasDocumentsAccess, organizationSettings]);
  useEffect(() => {
    setShowBulletinAction(!hasBulletinAccess && organizationSettings?.web?.ui?.requestedBulletinAccess !== true);
  }, [hasBulletinAccess, organizationSettings]);
  useEffect(() => {
    setShowDailyProjectReportAction(
      !hasDailyProjectReportAccess && organizationSettings?.web?.ui?.requestedDailyProjectReportsAccess !== true
    );
  }, [hasDailyProjectReportAccess, organizationSettings]);
  useEffect(() => {
    setShowProgressTrackingAction(
      !hasProgressTrackingAccess && organizationSettings?.web?.ui?.requestedProgressTrackingAccess !== true
    );
  }, [hasProgressTrackingAccess, organizationSettings]);

  const onDocumentRequestSubmitted = () => {
    setShowDocumentsAction(false);
    documentRequestCompleteOpenable.open();
    documentDialogOpenable.close();
  };

  const onBulletinRequestSubmitted = () => {
    setShowBulletinAction(false);
    bulletinRequestCompleteOpenable.open();
    bulletinDialogOpenable.close();
  };

  const onDailyProjectReportRequestSubmitted = () => {
    setShowDailyProjectReportAction(false);
    dailyProjectReportRequestCompleteOpenable.open();
    dailyProjectReportDialogOpenable.close();
  };

  const onProgressTrackingRequestSubmitted = () => {
    setShowProgressTrackingAction(false);
    progressTrackingRequestCompleteOpenable.open();
    progressTrackingDialogOpenable.close();
  };
  const onLinkClicked = (type: keyof typeof announcementLinksLookup) => {
    const link = announcementLinksLookup[type]!;
    window.open(link);
  };

  const renderTitle = () => (
    <Row align={Align.CENTER}>
      <p className="title">{t('Bulletin: Company Messaging Made Easy')}</p>
    </Row>
  );

  const renderIcon = () => <Icon className="announcement-icon" svg={BulletinIcon} />;

  const CurrentReleaseDetails = () => (
    <div className="content-column pb-5">
      {isPremiumTrial
        ? t(
            `Stop wasting time sending out hundreds of separate texts and emails. Put all your company's communication in one place - busybusy now has messaging! It's called Bulletin and this Premium feature is going to change the way your company communicates. Keep using Bulletin and all our amazing premium features. Upgrade your account from Trial to Premium today!`
          )
        : isPremium
        ? t(
            `Stop wasting time sending out hundreds of separate texts and emails. Put all your company's communication in one place - busybusy now has messaging! It's called Bulletin and this Premium feature is going to change the way your company communicates.`
          )
        : t(
            `Stop wasting time sending out hundreds of separate texts and emails. Put all your company's communication in one place - busybusy now has messaging! It's called Bulletin and it's going to change the way your company communicates. $2 per user/month, or part of `
          )}
      {!isPremium && !isPremiumTrial && (
        <span className="content">
          <a
            className="link"
            onClick={() => {
              graylogUpgradePremium();
              premiumDialogOpenable.open();
            }}
          >
            {t('Premium')}
          </a>
          .
        </span>
      )}

      <br />

      <div className="content-title mt-6">{t('Highlights')}</div>
      <ul className="p-0 ml-4 mb-8">
        <li>
          <span className="content">{t('Quickly Select Recipients')}</span>
        </li>
        <li>
          <span className="content">{t('Attach Photos and Files')}</span>
        </li>
        <li>
          <span className="content">{t('Request Acknowledgement')}</span>
        </li>
        <li>
          <span className="content">{t('Allow Comments')}</span>
        </li>
        <li>
          <span className="content">{t('And More!')}</span>
        </li>
      </ul>

      <ConditionalContainer condition={isPremiumTrial && !isPremium && canManageCompanySettings}>
        <Button
          className="mt-6"
          type="primary"
          onClick={() => {
            graylogUpgradePremium();
            premiumDialogOpenable.open();
          }}
        >
          {t('Go Premium')}
        </Button>
      </ConditionalContainer>
      <ConditionalContainer
        condition={isPro && !isPremium && !isPremiumTrial && canManageCompanySettings && showBulletinAction}
      >
        <Button
          className="mt-6"
          type="primary"
          onClick={() => {
            graylogEnableBulletin();
            bulletinDialogOpenable.open();
          }}
        >
          {t('Enable Bulletin')}
        </Button>
      </ConditionalContainer>
    </div>
  );

  const PreviousReleaseDetails = () => (
    <div>
      <div className="content-title">{t('Bulletin Overview')}</div>
      <Thumbnail
        className={'my-2'}
        fileUrl={video_thumbnail}
        onClick={() => {
          if (isPremiumTrial) {
            onLinkClicked('bulletinOverviewPremiumTrial');
          } else if (isPremium && !isPremiumTrial) {
            onLinkClicked('bulletinOverviewPremium');
          } else {
            onLinkClicked('bulletinOverviewFreeAndPro');
          }
        }}
      />

      <ConditionalContainer condition={isPro && !isPremium && canManageCompanySettings}>
        <div className="content-title">{t('Additional Upgrades')}</div>
        <ul>
          <li>
            <a
              className="link ml-1"
              onClick={() => {
                graylogUpgradePremium();
                premiumDialogOpenable.open();
              }}
            >
              {t('Premium')}
            </a>
          </li>
          {showDocumentsAction && (
            <li>
              <a
                className="link ml-1"
                onClick={() => {
                  graylogEnableDocuments();
                  documentDialogOpenable.open();
                }}
              >
                {t('Documents')}
              </a>
            </li>
          )}
          {showDailyProjectReportAction && (
            <li>
              <a
                className="link ml-1"
                onClick={() => {
                  graylogEnableDailyProjectReports();
                  dailyProjectReportDialogOpenable.open();
                }}
              >
                {'Daily Project Reports'}
              </a>
            </li>
          )}
          {showProgressTrackingAction && (
            <li>
              <a
                className="link ml-1"
                onClick={() => {
                  graylogEnableProgressTracking();
                  progressTrackingDialogOpenable.open();
                }}
              >
                {'Progress Tracking'}
              </a>
            </li>
          )}
        </ul>
      </ConditionalContainer>
    </div>
  );

  const renderContent = () => {
    return (
      <Row className="mt-2">
        <CurrentReleaseDetails />
        <div className="vertical-divider mx-10 mb-6" />
        <PreviousReleaseDetails />
      </Row>
    );
  };

  const renderAnnouncement = () => {
    return (
      <>
        <div className={classes}>
          <IconButton className="close-icon" svg={CloseIcon} onClick={onClose} />
          <Row align={Align.CENTER} className={theme}>
            {renderIcon()}
            {renderTitle()}
          </Row>
          {renderContent()}
        </div>

        <HeaderDialog
          isOpen={documentDialogOpenable.isOpen}
          onClose={documentDialogOpenable.close}
          divider={false}
          title={t('Documents')}
        >
          <DocumentEnableForm className="px-8 pb-9" onRequestSubmitted={onDocumentRequestSubmitted} />
        </HeaderDialog>
        <ConfirmationDialog
          title={t('Submitted')}
          message={t(
            'Your request has been submitted and Documents will soon be enabled for your account. We’ll send you a message letting you know when the feature is ready to use. Thank you!'
          )}
          isOpen={documentRequestCompleteOpenable.isOpen}
          confirmButtonText={t('OK')}
          onConfirm={documentRequestCompleteOpenable.close}
        />

        <HeaderDialog
          isOpen={bulletinDialogOpenable.isOpen}
          onClose={bulletinDialogOpenable.close}
          divider={false}
          title={t('Bulletin')}
        >
          <BulletinEnableForm className="px-8 pb-9" onRequestSubmitted={onBulletinRequestSubmitted} />
        </HeaderDialog>
        <ConfirmationDialog
          title={t('Submitted')}
          message={t(
            'Your request has been submitted and Bulletin will soon be enabled for your account. We’ll send you a message letting you know when the feature is ready to use. Thank you!'
          )}
          isOpen={bulletinRequestCompleteOpenable.isOpen}
          confirmButtonText={t('OK')}
          onConfirm={bulletinRequestCompleteOpenable.close}
        />

        <HeaderDialog
          isOpen={dailyProjectReportDialogOpenable.isOpen}
          onClose={dailyProjectReportDialogOpenable.close}
          divider={false}
          title={t('Daily Project Reports')}
        >
          <DailyProjectReportEnableForm
            className="px-8 pb-9"
            onRequestSubmitted={onDailyProjectReportRequestSubmitted}
          />
        </HeaderDialog>
        <ConfirmationDialog
          title={t('Submitted')}
          message={t(
            'Your request has been submitted and Daily Project Reports will soon be enabled for your account. We’ll send you a message letting you know when the feature is ready to use. Thank you!'
          )}
          isOpen={dailyProjectReportRequestCompleteOpenable.isOpen}
          confirmButtonText={t('OK')}
          onConfirm={dailyProjectReportRequestCompleteOpenable.close}
        />

        <HeaderDialog
          isOpen={progressTrackingDialogOpenable.isOpen}
          onClose={progressTrackingDialogOpenable.close}
          divider={false}
          title={t('Progress Tracking')}
        >
          <ProgressTrackingEnableForm className="px-8 pb-9" onRequestSubmitted={onProgressTrackingRequestSubmitted} />
        </HeaderDialog>
        <ConfirmationDialog
          title={t('Submitted')}
          message={t(
            'Your request has been submitted and Progress Tracking will soon be enabled for your account. We’ll send you a message letting you know when the feature is ready to use. Thank you!'
          )}
          isOpen={progressTrackingRequestCompleteOpenable.isOpen}
          confirmButtonText={t('OK')}
          onConfirm={progressTrackingRequestCompleteOpenable.close}
        />

        <HeaderDialog
          isOpen={premiumDialogOpenable.isOpen}
          onClose={premiumDialogOpenable.close}
          title={t('Additional Services')}
          size={'full'}
        >
          <EnablePremiumDialog />
        </HeaderDialog>
      </>
    );
  };

  return showAnnouncement ? renderAnnouncement() : <></>;
};

export default DashboardAnnouncement;
